import React, { useEffect, useState, useCallback } from "react";
import { Edit, Plus, RotateCw } from "lucide-react";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import ToolbarSearch from "../components/ToolbarSearch";
import OperatorCardActions from "../components/OperatorCardActions";
import {
    fetchOperatorNews,
    markOperatorNewsViewed,
    updateNewsItem,
    createNewsItem,
} from "../services/news";
import { fetchLocations } from "../services/locations";

const NewsOperators = ({ onNewsViewed }) => {
    const [news, setNews] = useState([]);
    const [filteredNews, setFilteredNews] = useState([]);
    const [locations, setLocations] = useState([]);
    const [message, setMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [inactiveFrom, setInactiveFrom] = useState("");
    const [inactiveTo, setInactiveTo] = useState("");
    const [sortBy, setSortBy] = useState("dtlm");
    const [sortDir, setSortDir] = useState("desc");
    const [expandedNewsId, setExpandedNewsId] = useState(null);
    const [editingNewsId, setEditingNewsId] = useState(null);
    const [editedData, setEditedData] = useState({
        title: "",
        content: "",
        status: 1,
        location_id: null,
    });
    const [originalData, setOriginalData] = useState({});
    const [newNews, setNewNews] = useState({
        title: "",
        content: "",
        status: 1,
        location_id: null,
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const loadNews = useCallback(async () => {
        try {
            const newsData = await fetchOperatorNews();
            setNews(newsData);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await markOperatorNewsViewed();
                if (!cancelled && typeof onNewsViewed === "function") onNewsViewed();
            } catch (_) {
                // ignore mark failures
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLocations = useCallback(async () => {
        try {
            const data = await fetchLocations();
            setLocations(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        loadNews();
        loadLocations();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [loadNews, loadLocations]);

    useEffect(() => {
        if (!Array.isArray(news) || news.length === 0) return;

        let raw = null;
        try {
            raw = localStorage.getItem("pendingNewsOpenId_operator");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const id = parseInt(raw);
        try {
            localStorage.removeItem("pendingNewsOpenId_operator");
        } catch (_) {
            // ignore
        }
        if (!Number.isFinite(id) || id <= 0) return;

        setSearchQuery("");
        setStatusFilter("");
        setInactiveFrom("");
        setInactiveTo("");
        setEditingNewsId(null);
        setExpandedNewsId(id);
    }, [news]);

    const normalizeText = (text) => (typeof text === "string" ? text : "");

    const buildLocationLabel = (loc) => {
        if (!loc) return "";
        return [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ");
    };

    const normalizeLocationId = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const n = parseInt(value);
        return Number.isFinite(n) ? n : null;
    };

    const getPreviewText = (text) => {
        const raw = normalizeText(text).replace(/\r\n/g, "\n").trim();
        if (!raw) return "Без контенту";
        const singleLine = raw.replace(/\n+/g, " ");
        return singleLine.length > 120 ? `${singleLine.slice(0, 120)}...` : singleLine;
    };

    const toggleExpanded = (id) => {
        if (editingNewsId === id) return;
        setExpandedNewsId((prev) => (prev === id ? null : id));
    };

    const handleEdit = (newsItem) => {
        setExpandedNewsId(newsItem.id);
        setEditingNewsId(newsItem.id);
        setEditedData({
            title: newsItem.title,
            content: newsItem.content,
            status: newsItem.status ? 1 : 0,
            location_id: newsItem.location_id ?? null,
        });
        setOriginalData({
            title: newsItem.title,
            content: newsItem.content,
            status: newsItem.status ? 1 : 0,
            location_id: newsItem.location_id ?? null,
        });
    };

    const handleCancel = () => {
        setEditedData(originalData);
        setEditingNewsId(null);
    };

    const parseDate = (dateStr, endOfDay = false) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return null;
        if (endOfDay) d.setHours(23, 59, 59, 999);
        else d.setHours(0, 0, 0, 0);
        return d;
    };

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const fromDate = parseDate(inactiveFrom, false);
        const toDate = parseDate(inactiveTo, true);
        const inactiveDateFilterEnabled = Boolean(inactiveFrom || inactiveTo);

        const filtered = news.filter((item) => {
            const matchesQuery =
                (item.title || "").toString().toLowerCase().includes(q) ||
                (item.content || "").toString().toLowerCase().includes(q);
            if (!matchesQuery) return false;

            const isActive = Boolean(item.status);
            if (statusFilter === "active" && !isActive) return false;
            if (statusFilter === "inactive" && isActive) return false;

            // Date range applies only to inactive items
            if (!isActive && inactiveDateFilterEnabled) {
                if (!item.dtlm) return false;
                const itemDate = new Date(item.dtlm);
                if (Number.isNaN(itemDate.getTime())) return false;
                if (fromDate && itemDate < fromDate) return false;
                if (toDate && itemDate > toDate) return false;
            }
            return true;
        });
        setFilteredNews(filtered);
    }, [news, searchQuery, statusFilter, inactiveFrom, inactiveTo]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredNews) return [];
        const sorted = [...filteredNews];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";

            if (sortBy === "dtlm") {
                valA = new Date(valA).getTime();
                valB = new Date(valB).getTime();
                if (sortDir === "asc") {
                    return valA - valB;
                }
                return valB - valA;
            }

            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            }
            return valA < valB ? 1 : valA > valB ? -1 : 0;
        });
        return sorted;
    };

    const handleSave = async (id) => {
        try {
            await updateNewsItem(id, {
                ...editedData,
                location_id: normalizeLocationId(editedData.location_id),
            });
            loadNews();
            setEditingNewsId(null);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const handleAddNews = async () => {
        if (!newNews.title.trim() || !newNews.content.trim()) {
            alert("Назва та контент не можуть бути порожніми!");
            return;
        }
        try {
            await createNewsItem({
                ...newNews,
                location_id: normalizeLocationId(newNews.location_id),
            });
            setNewNews({ title: "", content: "", status: 1, location_id: null });
            setShowAddModal(false);
            loadNews();
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const renderLocationSelect = (value, onChange) => (
        <select value={value ?? ""} onChange={onChange}>
            <option value="">Всі локації</option>
            {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                    {buildLocationLabel(loc) || loc.location_name}
                </option>
            ))}
        </select>
    );

    const renderAccordionList = () => (
        <div className="operator-accordion-list config-grid news-operator-grid">
            {getSortedData().map((item) => {
                const isEditing = editingNewsId === item.id;
                const isExpanded = isEditing || expandedNewsId === item.id;
                const statusLabel = item.status ? "Активна" : "Неактивна";
                const meta = [
                    item.location_name || "Всі локації",
                    statusLabel,
                    new Date(item.dtlm || Date.now()).toLocaleString("uk-UA"),
                ].filter(Boolean).join(" • ");

                return (
                    <div
                        key={item.id}
                        className={`operator-accordion-item config-card ${isExpanded ? "is-expanded" : ""} ${isEditing ? "is-editing" : ""}`}
                        onClick={() => {
                            if (isEditing) return;
                            toggleExpanded(item.id);
                        }}
                        onKeyDown={(e) => {
                            if (isEditing) return;
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleExpanded(item.id);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                    >
                        <div className="operator-accordion-head news-operator-card__top">
                            <div className="operator-accordion-head-main">
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editedData.title}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setEditedData({ ...editedData, title: e.target.value })}
                                    />
                                ) : (
                                    <div className="operator-accordion-title">{item.title}</div>
                                )}
                                <div className="operator-accordion-meta">{meta}</div>
                                {!isExpanded ? (
                                    <div className="operator-accordion-preview">{getPreviewText(item.content)}</div>
                                ) : null}
                            </div>
                            <div className="news-operator-card__actions" onClick={(e) => e.stopPropagation()}>
                                <OperatorCardActions
                                    editing={isEditing}
                                    stopPropagation
                                    onEdit={() => handleEdit(item)}
                                    onSave={() => handleSave(item.id)}
                                    onCancel={handleCancel}
                                />
                            </div>
                        </div>

                        {isExpanded ? (
                            <div className="operator-accordion-body" onClick={(e) => e.stopPropagation()}>
                                <div className="operator-detail-block">
                                    <strong>Контент:</strong>
                                    {isEditing ? (
                                        <textarea
                                            value={editedData.content}
                                            onChange={(e) => setEditedData({ ...editedData, content: e.target.value })}
                                            rows={6}
                                            style={{ width: "100%", marginTop: 6 }}
                                        />
                                    ) : (
                                        <div className="operator-detail-value" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
                                            {normalizeText(item.content) || "Без контенту"}
                                        </div>
                                    )}
                                </div>
                                <div className="operator-detail-block" style={{ marginTop: 10 }}>
                                    <strong>Локація:</strong>
                                    {isEditing ? (
                                        <div style={{ marginTop: 6 }}>
                                            {renderLocationSelect(editedData.location_id, (e) =>
                                                setEditedData({ ...editedData, location_id: e.target.value || null })
                                            )}
                                        </div>
                                    ) : (
                                        <div className="operator-detail-value" style={{ marginTop: 4 }}>
                                            {item.location_name || "Всі локації"}
                                        </div>
                                    )}
                                </div>
                                <div className="operator-detail-block" style={{ marginTop: 10 }}>
                                    <strong>Статус:</strong>
                                    {isEditing ? (
                                        <select
                                            value={editedData.status}
                                            onChange={(e) => setEditedData({ ...editedData, status: parseInt(e.target.value, 10) })}
                                            style={{ marginTop: 6, display: "block" }}
                                        >
                                            <option value={1}>Активна</option>
                                            <option value={0}>Неактивна</option>
                                        </select>
                                    ) : (
                                        <div className="operator-detail-value" style={{ marginTop: 4 }}>{statusLabel}</div>
                                    )}
                                </div>
                                {!isEditing ? (
                                    <div style={{ marginTop: 8 }}>
                                        <strong>Дата:</strong> {new Date(item.dtlm || Date.now()).toLocaleString("uk-UA")}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );

    const renderDesktopView = () => (
        <div className="table-responsive">
            <table>
                <thead>
                    <tr>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("title")}>
                            Заголовок {sortBy === "title" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("content")}>
                            Контент {sortBy === "content" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("location_id")}>
                            Локація {sortBy === "location_id" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                            Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("dtlm")}>
                            Дата {sortBy === "dtlm" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th>Дії</th>
                    </tr>
                </thead>
                <tbody>
                    {getSortedData().map((item) => {
                        const isExpanded = expandedNewsId === item.id;
                        return (
                            <React.Fragment key={item.id}>
                                <tr
                                    onClick={() => toggleExpanded(item.id)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <td>{item.title}</td>
                                    <td>
                                        <div className="news-operator-content preview" title="Натисніть, щоб розгорнути">
                                            <span className="news-operator-preview-text">{getPreviewText(item.content)}</span>
                                        </div>
                                    </td>
                                    <td>{item.location_name || "Всі локації"}</td>
                                    <td className="text-center">
                                        <span style={{ color: item.status ? "#4caf50" : "#f44336", fontWeight: "600" }}>
                                            {item.status ? "Активна" : "Неактивна"}
                                        </span>
                                    </td>
                                    <td className="text-center" style={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                                        {new Date(item.dtlm || Date.now()).toLocaleString("uk-UA")}
                                    </td>
                                    <td className="actions">
                                        <button className="icon-button" onClick={(e) => { e.stopPropagation(); handleEdit(item); }} title="Редагувати">
                                            <Edit size={16} />
                                        </button>
                                    </td>
                                </tr>
                                {isExpanded ? (
                                    <tr>
                                        <td colSpan={6}>
                                            <div className="operator-accordion-body" style={{ borderTop: "none" }}>
                                                <div style={{ whiteSpace: "pre-wrap" }}>
                                                    {normalizeText(item.content) || "Без контенту"}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const filterActive = Boolean(statusFilter || inactiveFrom || inactiveTo);

    const clearFilters = () => {
        setStatusFilter("");
        setInactiveFrom("");
        setInactiveTo("");
    };

    return (
        <div className="news-popup">
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadNews} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <FilterPopover
                        active={filterActive}
                        title="Фільтри новин"
                        hint="Статус фільтрує список. Період застосовується лише до неактивних новин за датою зміни."
                        buttonTitle="Фільтри новин"
                        onClear={clearFilters}
                    >
                        <label className="toolbar-filter-field">
                            <span>Статус</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="">Усі</option>
                                <option value="active">Активні</option>
                                <option value="inactive">Неактивні</option>
                            </select>
                        </label>
                        <DateRangeFields
                            from={inactiveFrom}
                            to={inactiveTo}
                            onFromChange={setInactiveFrom}
                            onToChange={setInactiveTo}
                            fromLabel="Неактивні: від"
                            toLabel="Неактивні: до"
                        />
                    </FilterPopover>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за заголовком або контентом..."
                    />
                </div>
            </div>

            {message && <p>{message}</p>}

            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content operator-mobile-modal operator-form-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати новину</h3>
                        <div className="operator-form-grid">
                            <input
                                className="operator-form-span-2"
                                type="text"
                                placeholder="Заголовок"
                                value={newNews.title}
                                onChange={(e) => setNewNews({ ...newNews, title: e.target.value })}
                            />
                            <textarea
                                className="operator-form-span-2"
                                placeholder="Контент"
                                value={newNews.content}
                                onChange={(e) => setNewNews({ ...newNews, content: e.target.value })}
                            />
                            <select
                                value={newNews.location_id ?? ""}
                                onChange={(e) => setNewNews({ ...newNews, location_id: e.target.value || null })}
                            >
                                <option value="">Всі локації</option>
                                {locations.map((loc) => (
                                    <option key={loc.id} value={loc.id}>
                                        {buildLocationLabel(loc) || loc.location_name}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={newNews.status}
                                onChange={(e) => setNewNews({ ...newNews, status: parseInt(e.target.value) })}
                            >
                                <option value={1}>Активна</option>
                                <option value={0}>Неактивна</option>
                            </select>
                        </div>
                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAddNews}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">Скасувати</button>
                        </div>
                    </div>
                </div>
            )}

            {isMobile ? renderAccordionList() : renderDesktopView()}
        </div>
    );
};

export default NewsOperators;
